import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Stack,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  PersonAddAlt1 as AssignIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import { UserContext } from "../../context/UserProvider";
import axios from "axios";
import { format } from "date-fns";
import { getHeader, toastError } from "../../utils/util";
import { API_ENDPOINT, USER_ROLES } from "../../utils/constants";
import useApiEp from "../../hooks/useApiEp";
import { toast } from "react-toastify";

const VoiceFileDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { user, loading: isUserLoading } = useContext(UserContext);

  const { data: customerIds = [], isLoading: loadingCustomers } =
    useApiEp("/api/customer-ids");

  const [voiceFile, setVoiceFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /* ---------------------------------
     Assign client states
  ---------------------------------- */
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState("");

  /* ---------------------------------
     ROOT / SUPER permission
  ---------------------------------- */
  const canAssignVoiceFile =
    user?.userType === USER_ROLES.ROOT || user?.userType === USER_ROLES.SUPER;

  /*
    Support both responses:

    [
      "CUSTOMER A",
      "CUSTOMER B"
    ]

    OR

    [
      { customerId: "CUSTOMER A" },
      { customerId: "CUSTOMER B" }
    ]
  */
  const normalizedCustomerIds = useMemo(() => {
    if (!Array.isArray(customerIds)) {
      return [];
    }

    return [
      ...new Set(
        customerIds
          .map((item) => {
            if (typeof item === "string") {
              return item;
            }

            return item?.customerId;
          })
          .filter(Boolean)
      ),
    ].sort((a, b) => a.localeCompare(b));
  }, [customerIds]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "success";

      case "rejected":
        return "error";

      case "pending":
        return "warning";

      // Backward compatibility
      case "completed":
        return "success";

      case "uploading":
        return "warning";

      case "failed":
        return "error";

      default:
        return "warning";
    }
  };

  /* ---------------------------------
     Fetch voice file
  ---------------------------------- */
  useEffect(() => {
    const fetchVoiceFile = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await axios.get(
          `${API_ENDPOINT}/voice/voice-files/${id}`,
          getHeader()
        );

        setVoiceFile(response.data.data);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch voice file details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchVoiceFile();
  }, [id]);

  /* ---------------------------------
     Open assign dialog
  ---------------------------------- */
  const handleOpenAssignDialog = () => {
    setSelectedCustomerIds([]);
    setAssignError("");
    setAssignDialogOpen(true);
  };

  const handleCloseAssignDialog = () => {
    if (assigning) return;

    setAssignDialogOpen(false);
    setSelectedCustomerIds([]);
    setAssignError("");
  };

  /* ---------------------------------
     Assign voice file
  ---------------------------------- */
  const handleAssignVoiceFile = async () => {
    if (!voiceFile?.voiceFileId) {
      setAssignError("Voice File ID is missing");
      return;
    }

    if (!selectedCustomerIds.length) {
      setAssignError("Please select at least one client");
      return;
    }

    try {
      setAssigning(true);
      setAssignError("");

      const response = await axios.post(
        `${API_ENDPOINT}/voice/assign-voice-file-id`,
        {
          voiceFileId: voiceFile.voiceFileId,
          customerIds: selectedCustomerIds,
        },
        getHeader()
      );

      const {
        insertedCount = 0,
        skippedCount = 0,
        message,
      } = response.data || {};

      toast.success(
        message ||
          `Voice file assigned to ${insertedCount} client${
            insertedCount !== 1 ? "s" : ""
          }`
      );

      if (skippedCount > 0) {
        toast.info(
          `${skippedCount} assignment${
            skippedCount !== 1 ? "s were" : " was"
          } already present and skipped`
        );
      }

      setAssignDialogOpen(false);
      setSelectedCustomerIds([]);
    } catch (err) {
      console.error("Assign voice file error:", err);

      const message =
        err.response?.data?.message || "Failed to assign voice file";

      setAssignError(message);

      if (toastError) {
        toastError(err);
      } else {
        toast.error(message);
      }
    } finally {
      setAssigning(false);
    }
  };

  /* ---------------------------------
     Loading
  ---------------------------------- */
  if (loading || isUserLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  /* ---------------------------------
     Error
  ---------------------------------- */
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>

        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: {
                xs: "flex-start",
                md: "center",
              },
              justifyContent: "space-between",
              flexDirection: {
                xs: "column",
                md: "row",
              },
              gap: 2,
              mb: 3,
            }}
          >
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
              Back to List
            </Button>

            {canAssignVoiceFile && (
              <Button
                variant="contained"
                startIcon={<AssignIcon />}
                onClick={handleOpenAssignDialog}
              >
                Assign Client
              </Button>
            )}
          </Box>

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Voice File Details
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            {/* File Name */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                File Name
              </Typography>

              <Typography variant="body1" fontWeight="medium">
                {voiceFile?.fileName || "-"}
              </Typography>
            </Grid>

            {/* Original File Name */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Original File Name
              </Typography>

              <Typography variant="body1">
                {voiceFile?.originalFileName || "-"}
              </Typography>
            </Grid>

            {/* Voice File ID */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Voice File ID
              </Typography>

              <Typography variant="body1">
                {voiceFile?.voiceFileId || "-"}
              </Typography>
            </Grid>

            {/* Customer */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Customer ID
              </Typography>

              <Typography variant="body1">
                {voiceFile?.customerId || "-"}
              </Typography>
            </Grid>

            {/* User ID */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                User ID
              </Typography>

              <Typography variant="body1">
                {voiceFile?.userId || "-"}
              </Typography>
            </Grid>

            {/* Prompt Category */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Prompt Category
              </Typography>

              <Chip
                label={voiceFile?.promptCategory || "-"}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Grid>

            {/* File Size */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                File Size
              </Typography>

              <Typography variant="body1">
                {voiceFile?.fileSizeReadable || "-"}
              </Typography>
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>

              <Chip
                label={voiceFile?.status || "Unknown"}
                size="small"
                color={getStatusColor(voiceFile?.status)}
                variant="filled"
                sx={{ mt: 0.5 }}
              />
            </Grid>

            {/* Uploaded At */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Uploaded At
              </Typography>

              <Typography variant="body1">
                {voiceFile?.createdAt
                  ? format(new Date(voiceFile.createdAt), "PPpp")
                  : "-"}
              </Typography>
            </Grid>

            {/* Uploaded By */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">
                Uploaded By
              </Typography>

              <Typography variant="body1">
                {voiceFile?.uploadedBy || "-"}
              </Typography>
            </Grid>

            {/* Error */}
            {voiceFile?.errorMessage && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Error Message
                </Typography>

                <Alert severity="error" sx={{ mt: 1 }}>
                  {voiceFile.errorMessage}
                </Alert>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>

      {/* =====================================
          ASSIGN CLIENT DIALOG
      ====================================== */}
      <Dialog
        open={assignDialogOpen}
        onClose={handleCloseAssignDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={700}>
            Assign Voice File
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Voice File ID: {voiceFile?.voiceFileId}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2.5}>
            <Alert severity="info">
              Select one or more clients. A copy of this voice file will be
              assigned to each selected client.
            </Alert>

            <Autocomplete
              multiple
              disableCloseOnSelect
              options={normalizedCustomerIds}
              value={selectedCustomerIds}
              onChange={(event, newValue) => {
                setSelectedCustomerIds(newValue);
                setAssignError("");
              }}
              loading={loadingCustomers}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Clients"
                  placeholder="Search customer ID..."
                  error={Boolean(assignError)}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                  />
                ))
              }
            />

            <Typography variant="body2" color="text.secondary">
              Selected: {selectedCustomerIds.length}
            </Typography>

            {assignError && <Alert severity="error">{assignError}</Alert>}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseAssignDialog} disabled={assigning}>
            Cancel
          </Button>

          <LoadingButton
            variant="contained"
            startIcon={<AssignIcon />}
            loading={assigning}
            disabled={
              assigning ||
              selectedCustomerIds.length === 0 ||
              !voiceFile?.voiceFileId
            }
            onClick={handleAssignVoiceFile}
          >
            Assign{" "}
            {selectedCustomerIds.length > 0
              ? `(${selectedCustomerIds.length})`
              : ""}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VoiceFileDetails;
